import React, { Suspense, useState, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { localStorage } from 'dibs-browser-storage';
import serverVars from 'server-vars';

import {
    showSuspectedTradeUserAcquisition,
    showSuspectedTradeTest,
    trackSuspectedTradeTest,
} from './helpers';
import { TRADE_STATUSES } from './../../../constants/userConstants';
import { VERTICALS } from './../../../constants/uriConstants';
import { TRADE_WINDFALL_REFERRAL_KEY } from './constants';

const SuspectedTradeUser = React.lazy(() =>
    import(/* webpackChunkName: "SuspectedTradeUser" */ './SuspectedTradeUser')
);

const MobileSuspectedTradeUser = React.lazy(() =>
    import(/* webpackChunkName: "MobileSuspectedTradeUser" */ './MobileSuspectedTradeUser')
);

function SuspectedTradeUserLoaderComponent({ relay, item, itemSearch, user, viewer }) {
    const [continueShowing, setContinueShowing] = useState(false);

    const tradeStatus = get(user, 'verifiedTradeStatus');
    const userId = get(user, 'serviceId');

    const pageType = serverVars.get('settings.pageType');
    const vertical = get(item, 'vertical') || get(itemSearch, 'topCategoryL1');
    const isValidVertical = [VERTICALS.ART, VERTICALS.FURNITURE].includes(vertical);
    const isQualifiedForSuspectedTradeTest = !userId && isValidVertical;
    const isSuspectedTradeTestVariant =
        isQualifiedForSuspectedTradeTest && showSuspectedTradeTest();

    useEffect(() => {
        if (isQualifiedForSuspectedTradeTest) {
            trackSuspectedTradeTest(pageType);
        }
    }, [isQualifiedForSuspectedTradeTest, pageType]);

    //only have the windfall flag supersede trade status if user has none
    const hasWindfallFlag = localStorage.getItem(TRADE_WINDFALL_REFERRAL_KEY) && !tradeStatus;
    const shouldShowSuspectedTrade =
        showSuspectedTradeUserAcquisition() &&
        (tradeStatus === TRADE_STATUSES.SUSPECTED ||
            continueShowing ||
            hasWindfallFlag ||
            isSuspectedTradeTestVariant);

    //if the suspected trade placement is shown this session, keep showing it regardless of prop changes
    if (shouldShowSuspectedTrade && !continueShowing) {
        setContinueShowing(true);
    }

    if (!shouldShowSuspectedTrade) {
        return null;
    }

    if (serverVars.get('settings.isMobile')) {
        return (
            <Suspense fallback="">
                <MobileSuspectedTradeUser viewer={viewer} userId={userId} />
            </Suspense>
        );
    }

    return (
        <Suspense fallback="">
            <SuspectedTradeUser
                relay={relay}
                viewer={viewer}
                userId={userId}
                onDismissAqcuisition={() => {
                    setContinueShowing(true);
                }}
                pageType={pageType}
                isSuspectedTradeTestVariant={isSuspectedTradeTestVariant}
            />
        </Suspense>
    );
}

SuspectedTradeUserLoaderComponent.propTypes = {
    relay: PropTypes.object,
    viewer: PropTypes.object,
    user: PropTypes.object,
    item: PropTypes.object,
    itemSearch: PropTypes.object,
};

export const SuspectedTradeUserLoader = createFragmentContainer(SuspectedTradeUserLoaderComponent, {
    viewer: graphql`
        fragment SuspectedTradeUserLoader_viewer on Viewer {
            ...SuspectedTradeUser_viewer
            ...MobileSuspectedTradeUser_viewer
        }
    `,
    user: graphql`
        fragment SuspectedTradeUserLoader_user on User {
            verifiedTradeStatus
            serviceId
        }
    `,
    item: graphql`
        fragment SuspectedTradeUserLoader_item on Item {
            vertical
        }
    `,
    itemSearch: graphql`
        fragment SuspectedTradeUserLoader_itemSearch on ItemSearchQueryConnection {
            topCategoryL1
        }
    `,
});
